import { iframeProcessor, imageProcessor } from '../processors';
import { type Replacement } from '../types';

/**
 * Generates a stable key from a replacement for comparison purposes.
 * This key is based on the actual replacement properties, not spanId,
 * since spanId uses UUIDs and changes every time the engine runs.
 * Uses the processor's getKey method to ensure consistency.
 *
 * @param replacement - The replacement to generate a key for
 * @returns A stable string key representing the replacement
 */
export const getReplacementKey = (replacement: Replacement): string => {
  if (replacement.type === 'image') {
    return imageProcessor.getKey(replacement);
  }

  if (replacement.type === 'iframe') {
    return iframeProcessor.getKey(replacement);
  }

  const unknownReplacement = replacement as { spanId: string; type: string };
  return `unknown:${unknownReplacement.type}:${unknownReplacement.spanId}`;
};

/**
 * Generates a stable key from an array of replacements for comparison.
 * This is used to detect if replacements have actually changed, preventing
 * infinite render loops when the array gets a new reference but contains the same content.
 *
 * @param replacements - Array of replacements to generate a key for
 * @returns A stable string key representing all replacements
 */
export const getReplacementsKey = (replacements: Array<Replacement>): string =>
  replacements.map(getReplacementKey).sort().join('|');
